import type { BBox } from 'geojson';

type MeshSize = 4 | 6 | 8 | 9 | 10 | 11 | 12;

type Point = {
  lat: number;
  lon: number;
};

type MeshCodeWithBasePoint = {
  meshCode: string;
  baseLat: number;
  baseLon: number;
};

type MeshCodeWithBounds = {
  meshCode: string;
  bbox: BBox;
};

// メッシュサイズごとの緯度経度の幅(度)
const MESH_UNITS = {
  4: { lat: 2 / 3, lon: 1 },
  6: { lat: 2 / 3 / 8, lon: 1 / 8 },
  8: { lat: 2 / 3 / 8 / 10, lon: 1 / 8 / 10 },
  9: { lat: 2 / 3 / 8 / 10 / 2, lon: 1 / 8 / 10 / 2 },
  10: { lat: 2 / 3 / 8 / 10 / 2 / 2, lon: 1 / 8 / 10 / 2 / 2 },
  11: { lat: 2 / 3 / 8 / 10 / 2 / 2 / 2, lon: 1 / 8 / 10 / 2 / 2 / 2 },
  12: { lat: 2 / 3 / 8 / 10 / 100, lon: 1 / 8 / 10 / 100 },
} as const satisfies Record<number, { lat: number; lon: number }>;

const toMeshSize = (meter: number): MeshSize => {
  switch (meter) {
    case 80000:
      return 4;
    case 10000:
      return 6;
    case 1000:
      return 8;
    case 500:
      return 9;
    case 250:
      return 10;
    case 125:
      return 11;
    case 10:
      return 12;
    default:
      throw new Error(`Invalid mesh size [${meter}] is given.`);
  }
};

const get80kmMeshCode = (point: Point): MeshCodeWithBasePoint => {
  const code1 = Math.floor(point.lat / MESH_UNITS[4].lat);
  const code2 = Math.floor(point.lon - 100);

  return {
    meshCode: `${code1}${code2}`,
    baseLat: code1 * MESH_UNITS[4].lat,
    baseLon: code2 + 100,
  };
};

const get10kmMeshCode = (point: Point): MeshCodeWithBasePoint => {
  const { meshCode, baseLat, baseLon } = get80kmMeshCode(point);

  const code3 = Math.floor((point.lat - baseLat) / MESH_UNITS[6].lat);
  const code4 = Math.floor((point.lon - baseLon) / MESH_UNITS[6].lon);

  return {
    meshCode: `${meshCode}${code3}${code4}`,
    baseLat: baseLat + code3 * MESH_UNITS[6].lat,
    baseLon: baseLon + code4 * MESH_UNITS[6].lon,
  };
};

const get1kmMeshCode = (point: Point): MeshCodeWithBasePoint => {
  const { meshCode, baseLat, baseLon } = get10kmMeshCode(point);

  const code5 = Math.floor((point.lat - baseLat) / MESH_UNITS[8].lat);
  const code6 = Math.floor((point.lon - baseLon) / MESH_UNITS[8].lon);

  return {
    meshCode: `${meshCode}${code5}${code6}`,
    baseLat: baseLat + code5 * MESH_UNITS[8].lat,
    baseLon: baseLon + code6 * MESH_UNITS[8].lon,
  };
};

const get500mMeshCode = (point: Point): MeshCodeWithBasePoint => {
  const { meshCode, baseLat, baseLon } = get1kmMeshCode(point);

  const code7 =
    (point.lat - baseLat >= MESH_UNITS[9].lat ? 2 : 0) +
    (point.lon - baseLon >= MESH_UNITS[9].lon ? 2 : 1);

  return {
    meshCode: `${meshCode}${code7}`,
    baseLat: baseLat + ([3, 4].includes(code7) ? MESH_UNITS[9].lat : 0),
    baseLon: baseLon + ([2, 4].includes(code7) ? MESH_UNITS[9].lon : 0),
  };
};

const get250mMeshCode = (point: Point): MeshCodeWithBasePoint => {
  const { meshCode, baseLat, baseLon } = get500mMeshCode(point);

  const code8 =
    (point.lat - baseLat >= MESH_UNITS[10].lat ? 2 : 0) +
    (point.lon - baseLon >= MESH_UNITS[10].lon ? 2 : 1);

  return {
    meshCode: `${meshCode}${code8}`,
    baseLat: baseLat + ([3, 4].includes(code8) ? MESH_UNITS[10].lat : 0),
    baseLon: baseLon + ([2, 4].includes(code8) ? MESH_UNITS[10].lon : 0),
  };
};

const get125mMeshCode = (point: Point): MeshCodeWithBasePoint => {
  const { meshCode, baseLat, baseLon } = get250mMeshCode(point);

  const code9 =
    (point.lat - baseLat >= MESH_UNITS[11].lat ? 2 : 0) +
    (point.lon - baseLon >= MESH_UNITS[11].lon ? 2 : 1);

  return {
    meshCode: `${meshCode}${code9}`,
    baseLat: baseLat + ([3, 4].includes(code9) ? MESH_UNITS[11].lat : 0),
    baseLon: baseLon + ([2, 4].includes(code9) ? MESH_UNITS[11].lon : 0),
  };
};

const get10mMeshCode = (point: Point): MeshCodeWithBasePoint => {
  const { meshCode, baseLat, baseLon } = get1kmMeshCode(point);

  const code7 = Math.floor((point.lat - baseLat) / MESH_UNITS[12].lat);
  const code8 = Math.floor((point.lon - baseLon) / MESH_UNITS[12].lon);

  return {
    meshCode: `${meshCode}${code7}${code8}`,
    baseLat: baseLat + code7 * MESH_UNITS[12].lat,
    baseLon: baseLon + code8 * MESH_UNITS[12].lon,
  };
};

const getMeshCodeWithBounds = (
  point: Point,
  meshSize: MeshSize
): MeshCodeWithBounds => {
  switch (meshSize) {
    case 4: {
      const { meshCode, baseLat, baseLon } = get80kmMeshCode(point);
      return {
        meshCode: meshCode,
        bbox: [
          baseLon,
          baseLat,
          baseLon + MESH_UNITS[4].lon,
          baseLat + MESH_UNITS[4].lat,
        ],
      };
    }
    case 6: {
      const { meshCode, baseLat, baseLon } = get10kmMeshCode(point);
      return {
        meshCode: meshCode,
        bbox: [
          baseLon,
          baseLat,
          baseLon + MESH_UNITS[6].lon,
          baseLat + MESH_UNITS[6].lat,
        ],
      };
    }
    case 8: {
      const { meshCode, baseLat, baseLon } = get1kmMeshCode(point);
      return {
        meshCode: meshCode,
        bbox: [
          baseLon,
          baseLat,
          baseLon + MESH_UNITS[8].lon,
          baseLat + MESH_UNITS[8].lat,
        ],
      };
    }
    case 9: {
      const { meshCode, baseLat, baseLon } = get500mMeshCode(point);
      return {
        meshCode: meshCode,
        bbox: [
          baseLon,
          baseLat,
          baseLon + MESH_UNITS[9].lon,
          baseLat + MESH_UNITS[9].lat,
        ],
      };
    }
    case 10: {
      const { meshCode, baseLat, baseLon } = get250mMeshCode(point);
      return {
        meshCode: meshCode,
        bbox: [
          baseLon,
          baseLat,
          baseLon + MESH_UNITS[10].lon,
          baseLat + MESH_UNITS[10].lat,
        ],
      };
    }
    case 11: {
      const { meshCode, baseLat, baseLon } = get125mMeshCode(point);
      return {
        meshCode: meshCode,
        bbox: [
          baseLon,
          baseLat,
          baseLon + MESH_UNITS[11].lon,
          baseLat + MESH_UNITS[11].lat,
        ],
      };
    }
    case 12: {
      const { meshCode, baseLat, baseLon } = get10mMeshCode(point);
      return {
        meshCode: meshCode,
        bbox: [
          baseLon,
          baseLat,
          baseLon + MESH_UNITS[12].lon,
          baseLat + MESH_UNITS[12].lat,
        ],
      };
    }
  }
};

export { toMeshSize, getMeshCodeWithBounds };
